import React from 'react';
import './AnimationToggle.css';

/**
 * AnimationToggle Component
 * Redesigned toggle for enabling/disabling animations throughout the site
 * Multiple variants available for design review
 */
const AnimationToggle = ({ 
  variant = 'original',
  enabled = true, 
  onChange,
  label = 'アニメーション',
  onRedBackground = false
}) => {
  const handleToggle = (value) => {
    if (onChange) {
      onChange(value);
    }
  };

  // Variant 1: Original Goodpatch-style (current implementation)
  if (variant === 'original') {
    return (
      <div className="animation-toggle animation-toggle--original">
        <span className="animation-toggle__label">{label}</span>
        <div className={`toggle-switch ${!enabled ? 'off' : ''}`}>
          <button 
            className={`toggle-btn ${enabled ? 'active' : ''}`}
            onClick={() => handleToggle(true)}
          >
            ON
          </button>
          <button 
            className={`toggle-btn ${!enabled ? 'active' : ''}`}
            onClick={() => handleToggle(false)}
          >
            OFF
          </button>
        </div>
      </div>
    );
  }

  // Variant 2: Minimalist Icon Toggle
  if (variant === 'minimalist') {
    return (
      <div className="animation-toggle animation-toggle--minimalist">
        <button 
          className={`toggle-icon ${enabled ? 'enabled' : 'disabled'}`}
          onClick={() => handleToggle(!enabled)}
          aria-label={`${label}: ${enabled ? 'ON' : 'OFF'}`}
          title={`${label}: ${enabled ? 'ON' : 'OFF'}`}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {enabled ? (
              // Play icon
              <polygon points="5 3 19 12 5 21 5 3" fill="currentColor" stroke="none" />
            ) : (
              // Pause icon
              <>
                <rect x="6" y="4" width="4" height="16" fill="currentColor" stroke="none" />
                <rect x="14" y="4" width="4" height="16" fill="currentColor" stroke="none" />
              </>
            )}
          </svg>
        </button>
      </div>
    );
  }

  // Variant 3: Slide Toggle with Icon
  if (variant === 'slide') {
    return (
      <div className="animation-toggle animation-toggle--slide">
        <span className="animation-toggle__label">{label}</span>
        <button
          className={`slide-toggle ${enabled ? 'enabled' : 'disabled'}`}
          onClick={() => handleToggle(!enabled)}
          aria-label={`${label}: ${enabled ? 'ON' : 'OFF'}`}
        >
          <span className="slide-toggle__track">
            <span className="slide-toggle__thumb">
              <svg viewBox="0 0 12 12" fill="currentColor">
                {enabled ? (
                  <path d="M3 2L9 6L3 10V2Z" />
                ) : (
                  <>
                    <rect x="2" y="2" width="3" height="8" />
                    <rect x="7" y="2" width="3" height="8" />
                  </>
                )}
              </svg>
            </span>
          </span>
        </button>
      </div>
    );
  }

  // Variant 4: Checkbox Style
  if (variant === 'checkbox') {
    return (
      <div className="animation-toggle animation-toggle--checkbox">
        <label className="checkbox-toggle">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => handleToggle(e.target.checked)}
          />
          <span className="checkbox-toggle__slider">
            <span className="checkbox-toggle__icon">
              <svg viewBox="0 0 16 16" fill="currentColor">
                <path d="M3 2L11 8L3 14V2Z" />
              </svg>
            </span>
          </span>
          <span className="checkbox-toggle__label">{label}</span>
        </label>
      </div>
    );
  }

  // Variant 5: Segmented Control (iOS-style)
  if (variant === 'segmented') {
    return (
      <div className="animation-toggle animation-toggle--segmented">
        <div className="segmented-control">
          <button
            className={`segmented-control__option ${enabled ? 'active' : ''}`}
            onClick={() => handleToggle(true)}
          >
            <svg viewBox="0 0 16 16" fill="currentColor" width="12" height="12">
              <path d="M3 2L11 8L3 14V2Z" />
            </svg>
          </button>
          <button
            className={`segmented-control__option ${!enabled ? 'active' : ''}`}
            onClick={() => handleToggle(false)}
          >
            <svg viewBox="0 0 16 16" fill="currentColor" width="12" height="12">
              <rect x="2" y="2" width="4" height="12" />
              <rect x="10" y="2" width="4" height="12" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  // Variant 6: Power Button Style
  if (variant === 'power') {
    return (
      <div className={`animation-toggle animation-toggle--power ${onRedBackground ? 'on-red-bg' : ''}`}>
        <button
          className={`power-button ${enabled ? 'on' : 'off'}`}
          onClick={() => handleToggle(!enabled)}
          aria-label={`${label}: ${enabled ? 'ON' : 'OFF'}`}
          title={`${label}`}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M12 2v10M18.36 6.64a9 9 0 1 1-12.73 0" strokeLinecap="round" />
          </svg>
        </button>
        <span className="power-button__label">{label}</span>
      </div>
    );
  }

  return null;
};

export default AnimationToggle;
